import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { waitingListSchema } from '@margembie-labradors/shared';
import { type User } from '@/api/entities/users';
import { useUpdateUser } from './api/useUpdateUser';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { COLOR_CHECKBOX_OPTIONS } from '@/shared/constants';
import type { IWaitingListFormInputs } from '@margembie-labradors/shared';

type EditUserFormData = IWaitingListFormInputs;

type Props = {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function EditUserDialog({ user, open, onOpenChange }: Props) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<EditUserFormData>({
    resolver: zodResolver(waitingListSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      sexPreference: '',
      colorPreference: [],
      type: '',
      ranking: undefined,
      comment: '',
    },
  });

  const colorPreferenceControl = useFieldArray({
    control,
    name: 'colorPreference',
  });

  const mutation = useUpdateUser({
    config: {
      onSuccess: () => {
        toast({
          title: 'Success',
          description: 'User updated successfully',
        });
        onOpenChange(false);
      },
      onError: (error: Error) => {
        console.error('Mutation error:', error);
        toast({
          title: 'Error',
          description: error.message || 'Failed to update user',
          variant: 'destructive',
        });
      },
    },
  });

  useEffect(() => {
    if (user && open) {
      reset({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        sexPreference: user.sexPreference?.[0] || '',
        colorPreference: user.colorPreference?.map((cp) => ({ value: cp })) || [],
        type: user.type || '',
        ranking: user.ranking,
        comment: user.comment || '',
      });
    }
  }, [user, open, reset]);

  const onSubmit = (data: EditUserFormData) => {
    if (!user) {
      toast({
        title: 'Error',
        description: 'No user selected',
        variant: 'destructive',
      });
      return;
    }

    const payload: Record<string, unknown> = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      sexPreference: [data.sexPreference],
      colorPreference: data.colorPreference.map((cp) => cp.value),
      type: data.type,
    };
    if (data.ranking !== undefined && data.ranking !== null) {
      payload.ranking = data.ranking;
    }
    if (data.comment) {
      payload.comment = data.comment;
    }

    mutation.mutate({ id: user.id, input: payload });
  };

  const sexPreferenceValue = watch('sexPreference');
  const typeValue = watch('type');
  const colorPreferences = watch('colorPreference') || [];

  const handleColorToggle = (colorValue: string, checked: boolean) => {
    const currentColors = colorPreferences || [];
    if (checked) {
      if (colorValue === 'any') {
        colorPreferenceControl.replace([{ value: 'any' }]);
      } else {
        const filteredColors = currentColors.filter((c) => c.value !== 'any');
        if (!filteredColors.some((c) => c.value === colorValue)) {
          colorPreferenceControl.replace([...filteredColors, { value: colorValue }]);
        }
      }
    } else {
      const index = currentColors.findIndex((c) => c.value === colorValue);
      if (index !== -1) {
        colorPreferenceControl.remove(index);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>Update the user's information below.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">
                First Name <span className="text-destructive">*</span>
              </Label>
              <Input id="firstName" {...register('firstName')} placeholder="Enter first name" />
              {errors.firstName && (
                <p className="text-sm text-destructive">{errors.firstName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">
                Last Name <span className="text-destructive">*</span>
              </Label>
              <Input id="lastName" {...register('lastName')} placeholder="Enter last name" />
              {errors.lastName && (
                <p className="text-sm text-destructive">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">
              Email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              {...register('email')}
              placeholder="Enter email address"
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">
              Phone <span className="text-destructive">*</span>
            </Label>
            <Input id="phone" type="tel" {...register('phone')} placeholder="Enter phone number" />
            {errors.phone && <p className="text-sm text-destructive">{errors.phone.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="sexPreference">
              Sex Preference <span className="text-destructive">*</span>
            </Label>
            <Select
              value={sexPreferenceValue || ''}
              onValueChange={(value) => setValue('sexPreference', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="any">Any</SelectItem>
              </SelectContent>
            </Select>
            {errors.sexPreference && (
              <p className="text-sm text-destructive">{errors.sexPreference.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>
              Color Preference <span className="text-destructive">*</span>
            </Label>
            <div className="space-y-2">
              {COLOR_CHECKBOX_OPTIONS.map((colorOption) => (
                <div key={colorOption.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`color-${colorOption.value}`}
                    checked={colorPreferences.some((c) => c.value === colorOption.value)}
                    disabled={
                      colorOption.value !== 'any' && colorPreferences.some((c) => c.value === 'any')
                    }
                    onChange={(e) => handleColorToggle(colorOption.value, e.target.checked)}
                  />
                  <Label
                    htmlFor={`color-${colorOption.value}`}
                    className="font-normal cursor-pointer"
                  >
                    {colorOption.label}
                  </Label>
                </div>
              ))}
            </div>
            {errors.colorPreference && (
              <p className="text-sm text-destructive">{errors.colorPreference.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">
              Type <span className="text-destructive">*</span>
            </Label>
            <Select value={typeValue || ''} onValueChange={(value) => setValue('type', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="first-timer">
                  First Time adopting from Margembie Labradors
                </SelectItem>
                <SelectItem value="breeder">Registered Breeder</SelectItem>
                <SelectItem value="returning-family">
                  Returning Margembie Labradors Family
                </SelectItem>
              </SelectContent>
            </Select>
            {errors.type && <p className="text-sm text-destructive">{errors.type.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="ranking">Ranking</Label>
            <Input
              id="ranking"
              type="number"
              min={0}
              {...register('ranking', {
                setValueAs: (value) => {
                  if (value === '' || value === null || value === undefined) {
                    return undefined;
                  }
                  const num = Number(value);
                  return isNaN(num) ? undefined : num;
                },
              })}
              placeholder="Enter ranking (optional)"
            />
            {errors.ranking && <p className="text-sm text-destructive">{errors.ranking.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="comment">Comments</Label>
            <Textarea
              id="comment"
              {...register('comment')}
              placeholder="Enter comments (optional)"
              rows={3}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || mutation.isPending}>
              {isSubmitting || mutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
